import { useState, useCallback, useRef } from "react";
import { useWeighbridgeCapture } from "./useWeighbridgeCapture";
import { captureVehicleImage, getSetupGroups } from "../pages/generic/crud";
import { SetupGroupSelectModal } from "../components/weighbridge/SetupGroupSelectModal";

// Lingering pause after both tracks finish so the user actually sees them turn
// green (a "done" flash) before the dialog closes.
const DONE_LINGER_MS = 650;

// Sentinel returned by the group picker when the user cancels the popup.
const CANCELLED = Symbol("capture-cancelled");

/**
 * Orchestrates a vehicle capture — weight (live, via the weighbridge) and the
 * vehicle photo (one-shot, via the camera) — running both in parallel and
 * exposing live per-track status so a single dialog can show both at once.
 *
 * Setup groups: a site may have multiple weighing-scale + camera pairs. Before
 * capturing, this hook loads the enabled setup groups. With 0 or 1 group it
 * proceeds silently (using that group, or legacy config when none). With MORE
 * than one, it shows `SetupGroupSelectModal` (returned as `groupSelectModal` for
 * the caller to render) and reads weight + image ONLY from the chosen group.
 * The pick is asked on EVERY capture — there is no sticky memory.
 *
 * Usage:
 *   const { capture, isCapturing, weight, image, groupSelectModal } = useVehicleCapture(active);
 *   const res = await capture({ weight: true, image: true });
 *   if (res.cancelled) return;                // user dismissed the group popup
 *   // res = { weight, image, weightError, imageError }
 *   <WeighbridgeCaptureModal show={isCapturing} weight={weight} image={image} />
 *   {groupSelectModal}
 */
export function useVehicleCapture(active) {
    const { captureWeight, captureInfo } = useWeighbridgeCapture(active);
    const [open, setOpen] = useState(false);
    // status: 'idle' | 'active' | 'done' | 'failed'
    const [weightTrack, setWeightTrack] = useState({ enabled: false, status: "idle", value: null });
    const [imageTrack, setImageTrack] = useState({ enabled: false, status: "idle", url: null });
    // Group-selection popup state; resolveRef holds the pending picker promise.
    const [selectState, setSelectState] = useState({ show: false, groups: [] });
    const resolveRef = useRef(null);

    // Resolve which setup group to capture from. Returns the chosen group, null
    // (no groups -> legacy single-device), or the CANCELLED sentinel.
    const chooseGroup = useCallback(async () => {
        let enabled = [];
        try {
            const { data } = await getSetupGroups();
            enabled = (Array.isArray(data) ? data : []).filter((g) => g && g.enabled);
        } catch (e) {
            // Backend without setup groups yet -> fall through to legacy behavior.
            enabled = [];
        }
        if (enabled.length <= 1) return enabled[0] || null;
        return new Promise((resolve) => {
            resolveRef.current = resolve;
            setSelectState({ show: true, groups: enabled });
        });
    }, []);

    const finishSelect = useCallback((value) => {
        setSelectState({ show: false, groups: [] });
        const resolve = resolveRef.current;
        resolveRef.current = null;
        if (resolve) resolve(value);
    }, []);

    const capture = useCallback(
        async ({ weight = false, image = false, imageFn } = {}) => {
            const chosen = await chooseGroup();
            if (chosen === CANCELLED) {
                return { weight: null, image: null, weightError: null, imageError: null, cancelled: true };
            }
            const group = chosen || null;
            const groupId = group ? group.primaryId || group.id : null;
            const weighbridge = group ? group.weighbridge : null;

            setOpen(true);
            setWeightTrack({ enabled: weight, status: weight ? "active" : "idle", value: null });
            setImageTrack({ enabled: image, status: image ? "active" : "idle", url: null });

            const result = { weight: null, image: null, weightError: null, imageError: null };
            const tasks = [];

            if (weight) {
                tasks.push(
                    captureWeight({ weighbridge, groupId })
                        .then((w) => {
                            result.weight = w;
                            setWeightTrack((t) => ({ ...t, status: "done", value: w }));
                        })
                        .catch((e) => {
                            result.weightError = e;
                            setWeightTrack((t) => ({ ...t, status: "failed" }));
                        })
                );
            }

            if (image) {
                // Default to the in-memory grab from the chosen group's camera;
                // callers that save immediately (e.g. captureAndSaveImage) pass
                // their own imageFn. Either way, forward the chosen groupId so the
                // image is read from the SELECTED group's camera (null -> legacy).
                const fn = imageFn || captureVehicleImage;
                tasks.push(
                    fn(groupId)
                        .then(({ data }) => {
                            const ok = data && data.success && (data.imageData || data.primaryId);
                            if (ok) {
                                const contentType = data.contentType || "image/jpeg";
                                result.image = { ...data, contentType };
                                const url =
                                    data.localUrl ||
                                    (data.imageData ? `data:${contentType};base64,${data.imageData}` : null);
                                setImageTrack((t) => ({ ...t, status: "done", url }));
                            } else {
                                result.imageError = new Error((data && data.message) || "No image returned");
                                setImageTrack((t) => ({ ...t, status: "failed" }));
                            }
                        })
                        .catch((e) => {
                            result.imageError = e;
                            setImageTrack((t) => ({ ...t, status: "failed" }));
                        })
                );
            }

            await Promise.all(tasks);
            // Let the green check(s) show briefly before closing.
            await new Promise((r) => setTimeout(r, DONE_LINGER_MS));
            setOpen(false);
            return result;
        },
        [captureWeight, chooseGroup]
    );

    // `info` (live phase/reading/progress) is merged in at render — no extra state.
    const weight = { ...weightTrack, info: captureInfo };
    const groupSelectModal = (
        <SetupGroupSelectModal
            show={selectState.show}
            groups={selectState.groups}
            onSelect={(g) => finishSelect(g)}
            onCancel={() => finishSelect(CANCELLED)}
        />
    );
    return { capture, isCapturing: open, weight, image: imageTrack, groupSelectModal };
}
